"use client";

import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import Image from "next/image";
import Link from "next/link";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Header 5001 — BDU site header.
 *
 * Port of the BDU v1 header: a white bar with the university crest on the left and,
 * on the right, a bold phone line, a green WhatsApp button and a red Apply button.
 *
 * BREAKPOINTS — v1 renders three separate bars (desktop / tablet / mobile). This is a
 * single bar instead, because the only differences were the logo size, the paddings and
 * which right-hand items showed. The switch points are v1's, in raw pixels:
 *
 *   <860px          logo 140×38, 12px gutters, Apply only          (`min-[860px]:hidden` bar)
 *   860–1023px      logo 140×32, 20px left / 12px right, Apply     (`min-[860px]:block lg:hidden` bar)
 *   ≥1024px         logo 180×42, 20px left / 40px right, 65px tall,
 *                   phone line + WhatsApp + Apply                  (`lg:block` bar)
 *
 * They are written as `min-[860px]:` / `min-[1024px]:` arbitrary variants, NOT as `tab:`
 * and `lg:` — campus-portal remaps the named scale (tab 768, lg 1430), so the named
 * variants would flip the layout hundreds of pixels away from where bduedu.in does and
 * leave a 1024–1429px viewport on the compact bar.
 *
 * The phone line is rendered verbatim from `phone_number`, so the CMS holds the whole
 * string ("Phone NO. +91-8956667205") exactly as v1 printed it; the `tel:` href is
 * derived from its digits.
 *
 * The Apply CTA has no toggle and no link field: it renders whenever `apply_button_text`
 * is non-empty, and clicking it opens the lead-drop form popup.
 */

const DEFAULT_BAR_BG = "#FFFFFF";
const DEFAULT_TEXT = "#272323";
const DEFAULT_WHATSAPP_BG = "#4CAF51";
const DEFAULT_APPLY_BG = "#BE2330";

interface Header5001Data {
  background_color?: string;
  show_bar_shadow?: boolean | number | string;
  sticky_to_top?: boolean | number | string;
  show_on_mobile?: boolean | number | string;

  show_logo?: boolean | number | string;
  logo_image?: string;
  logo_alt_text?: string;

  phone_number?: string;
  phone_text_color?: string;
  phone_icon?: string;
  phone_icon_alt?: string;

  whatsapp_show?: boolean | number | string;
  whatsapp_number?: string;
  whatsapp_link?: string;
  whatsapp_button_text?: string;
  whatsapp_button_color?: string;
  whatsapp_button_text_color?: string;
  whatsapp_icon?: string;

  apply_button_text?: string;
  button_color?: string;
  button_text_color?: string;

  status?: boolean | number | string;
}

/** CMS booleans arrive as true/false, 1/0 or "1"/"0" depending on the write path. */
function isOn(value: unknown, fallback = true): boolean {
  if (value === undefined || value === null || value === "") return fallback;
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const s = String(value).trim().toLowerCase();
  if (["false", "0", "no", "off"].includes(s)) return false;
  if (["true", "1", "yes", "on"].includes(s)) return true;
  return fallback;
}

function str(v: unknown): string {
  return typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim();
}

/** Remote logos bypass the Next image optimiser, which only knows configured hosts. */
function isRemote(src: string): boolean {
  return /^https?:\/\//i.test(src);
}

function telHref(display: string): string {
  const digits = display.replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : "";
}

function whatsAppHref(link: string, number: string): string {
  if (link) return link;
  const digits = number.replace(/[^\d]/g, "");
  return digits
    ? `https://api.whatsapp.com/send/?phone=${digits}&text&type=phone_number&app_absent=0`
    : "";
}

function PhoneGlyph({ color }: { color: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M16.8323 17.875C14.7634 17.875 12.7194 17.4242 10.7002 16.5225C8.68096 15.6208 6.84381 14.342 5.18872 12.6863C3.53362 11.0305 2.25523 9.19338 1.35353 7.17483C0.45184 5.15628 0.000662037 3.11224 0 1.04271C0 0.744792 0.0993055 0.496528 0.297917 0.297917C0.496528 0.0993055 0.744792 0 1.04271 0H5.06458C5.2963 0 5.50318 0.0787823 5.68524 0.236347C5.8673 0.393912 5.97488 0.579944 6.00799 0.794444L6.65347 4.27014C6.68657 4.53495 6.6783 4.75839 6.62865 4.94045C6.57899 5.12251 6.48796 5.27975 6.35556 5.41215L3.9474 7.84514C4.27841 8.45752 4.67133 9.04905 5.12615 9.61973C5.58097 10.1904 6.0818 10.7409 6.62865 11.2712C7.14173 11.7843 7.67963 12.2603 8.24236 12.6992C8.80509 13.1381 9.40093 13.5393 10.0299 13.9028L12.3635 11.5691C12.5125 11.4201 12.7071 11.3086 12.9475 11.2344C13.1878 11.1603 13.4235 11.1394 13.6545 11.1719L17.0806 11.867C17.3123 11.9332 17.5026 12.0534 17.6516 12.2275C17.8005 12.4016 17.875 12.5959 17.875 12.8104V16.8323C17.875 17.1302 17.7757 17.3785 17.5771 17.5771C17.3785 17.7757 17.1302 17.875 16.8323 17.875Z"
        fill={color}
      />
    </svg>
  );
}

function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.5 14.4c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.46 1.32 4.96L2 22l5.25-1.38a9.86 9.86 0 0 0 4.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.84 9.84 0 0 0 12.04 2zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.69 8.24-8.24 8.24z" />
    </svg>
  );
}

function Header5001({ data }: { data: any }) {
  const raw = data as Record<string, unknown> | undefined;
  const isPreview = Boolean(raw?.isPreview ?? raw?.is_preview);
  const sectionData = (raw?.data ?? raw) as Header5001Data | null;
  const dispatch = useDispatch();

  // No toggle and no link: the Apply CTA shows only when it has text, and opens the form.
  const applyText = str(sectionData?.apply_button_text);
  const showApply = !!applyText;

  const handleApplyClick = useCallback(() => {
    ctaClick({
      section: "header_5001",
      component: "header_apply_now",
      label: applyText || "apply_now",
      step: "1",
      entity: "header_5001",
      parentEntity: "header_5001",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [applyText, dispatch]);

  if (!sectionData || !isOn(sectionData.status)) return null;

  const {
    background_color = DEFAULT_BAR_BG,
    logo_image,
    logo_alt_text,
    phone_number,
    phone_text_color = DEFAULT_TEXT,
    phone_icon,
    phone_icon_alt,
    whatsapp_number,
    whatsapp_link,
    whatsapp_button_text = "WhatsApp Chat",
    whatsapp_button_color = DEFAULT_WHATSAPP_BG,
    whatsapp_button_text_color = "#FFFFFF",
    whatsapp_icon,
    button_color = DEFAULT_APPLY_BG,
    button_text_color = "#FFFFFF",
  } = sectionData;

  const logoSrc = str(logo_image);
  const showLogo = isOn(sectionData.show_logo) && !!logoSrc;

  const phoneText = str(phone_number);
  const phoneHref = telHref(phoneText);

  const waHref = whatsAppHref(str(whatsapp_link), str(whatsapp_number));
  const showWhatsApp = isOn(sectionData.whatsapp_show) && !!waHref;
  const waIconSrc = str(whatsapp_icon);
  const phoneIconSrc = str(phone_icon);

  // Sticky is the reference behaviour, but a fixed bar inside the admin's scaled
  // preview pane would float over the editor chrome, so the preview stays in flow.
  const sticky = isOn(sectionData.sticky_to_top) && !isPreview;

  return (
    <header
      className={cn(
        "z-50 w-full",
        sticky && "sticky top-0",
        isOn(sectionData.show_bar_shadow, false) && "shadow-sm",
        !isOn(sectionData.show_on_mobile) && "hidden min-[860px]:block",
      )}
      style={{ backgroundColor: str(background_color) || DEFAULT_BAR_BG }}
    >
      <div className="mx-auto flex h-16 max-w-[2024px] items-center justify-between gap-3 pl-3 pr-3 min-[860px]:pl-5 min-[1024px]:h-[65px] min-[1024px]:pr-10">
        {showLogo ? (
          <Image
            src={logoSrc}
            alt={str(logo_alt_text) || "University"}
            width={180}
            height={42}
            priority
            unoptimized={isRemote(logoSrc)}
            className="h-[38px] w-[140px] object-contain min-[860px]:h-8 min-[1024px]:h-[42px] min-[1024px]:w-[180px]"
          />
        ) : (
          <span />
        )}

        <div className="flex items-center justify-end gap-[10px]">
          {phoneText ? (
            <Link
              href={phoneHref || "#"}
              className="hidden items-center gap-2 whitespace-nowrap min-[1024px]:flex"
              style={{ color: str(phone_text_color) || DEFAULT_TEXT }}
            >
              {phoneIconSrc ? (
                <Image
                  src={phoneIconSrc}
                  alt={str(phone_icon_alt) || "Phone"}
                  width={18}
                  height={18}
                  unoptimized={isRemote(phoneIconSrc)}
                  className="size-[18px] object-contain"
                />
              ) : (
                <PhoneGlyph color={str(phone_text_color) || DEFAULT_TEXT} />
              )}
              <span className="text-[13px] font-semibold">{phoneText}</span>
            </Link>
          ) : null}

          {showApply ? (
            <button
              type="button"
              onClick={handleApplyClick}
              className="flex h-9 cursor-pointer items-center rounded border-none px-5 text-[16px]/[24px] font-medium shadow"
              style={{
                background: str(button_color) || DEFAULT_APPLY_BG,
                color: str(button_text_color) || "#FFFFFF",
              }}
            >
              {applyText}
            </button>
          ) : null}

          {showWhatsApp ? (
            <Link
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden h-7 items-center gap-2 whitespace-nowrap rounded-md px-5 text-base font-medium min-[1024px]:flex"
              style={{
                backgroundColor: str(whatsapp_button_color) || DEFAULT_WHATSAPP_BG,
                color: str(whatsapp_button_text_color) || "#FFFFFF",
              }}
            >
              {waIconSrc ? (
                <Image
                  src={waIconSrc}
                  alt={str(whatsapp_button_text) || "WhatsApp"}
                  width={20}
                  height={20}
                  unoptimized={isRemote(waIconSrc)}
                  className="size-5 object-contain"
                />
              ) : (
                <WhatsAppGlyph className="size-5" />
              )}
              {str(whatsapp_button_text) || "WhatsApp Chat"}
            </Link>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export default Header5001;
